import React, { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import styled from 'styled-components';

import { Box, CircularProgress, InputAdornment } from '@mui/material';
import { useTheme } from '../../styles';
import { AutocompleteContainer, AutocompleteInput, AutocompletePopper } from './Base';
import { VolumeUpOutlined } from '@mui/icons-material';
import { getTTSVoiceParams, TTSVoice } from '@requests/telnyx';
import { formatKokoroVoiceName, TTSVoiceListItem } from './ListItem/TTSVoiceListItem';
import { RequestError } from '@requests/common';
import { useQuery } from '@tanstack/react-query';
import { getTTSVoices } from '@requests/telnyx/tts-voices.requests';
import { ErrorMessage } from './Models';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  & .MuiInputBase-adornedStart {
    padding: 0 9px !important;
  }
`;

export type AzureVoiceOption = {
  label: string;
  value: string;
  language_code: string;
  gender: string;
};

interface Props {
  value: string;
  provider: 'azure' | 'elevenlabs' | 'telnyx' | 'aws';
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: TTSVoice | null) => void;
  disabled?: boolean;
  placeholder?: string;
  elevenlabs_api_key_ref?: string;
}

export const TTSVoiceAutocomplete: React.FC<Props> = ({
  value,
  provider,
  onChange,
  disabled,
  placeholder,
  elevenlabs_api_key_ref,
}) => {
  const autocompleteRef = useRef(null);
  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);

  const {
    data: options = [],
    isLoading: loading,
    error,
  } = useQuery<TTSVoice[], RequestError, TTSVoice[], [string, getTTSVoiceParams]>({
    queryKey: ['tts-voices', { provider, elevenlabs_api_key_ref }],
    queryFn: getTTSVoices,
    retry: false,
  });

  return (
    <Container>
      <AutocompleteContainer open={open}>
        <Autocomplete
          value={options.find((item) => item?.value === value) || null}
          ref={autocompleteRef}
          onChange={(event, newValue) => {
            onChange(event, newValue || null);
            setOpen(false);

            if (newValue) (document?.activeElement as HTMLElement)?.blur();
          }}
          disabled={disabled}
          PopperComponent={AutocompletePopper}
          renderOption={(props, option) => <TTSVoiceListItem {...props} option={option} />}
          autoHighlight
          options={options}
          disablePortal
          getOptionLabel={(option) =>
            option.provider === 'telnyx' ? formatKokoroVoiceName(option) : option.name || ''
          }
          getOptionKey={(option) => option.id}
          renderInput={(params) => (
            <AutocompleteInput
              open={open}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
              startAdornment={
                value &&
                !loading && (
                  <InputAdornment position='start'>
                    <Box
                      component='span'
                      sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        borderRadius: '3px',
                        ml: 0.31,
                        mt: '0px',
                      }}
                    >
                      <VolumeUpOutlined sx={{ fontSize: '20px', color: theme.primary }} />
                    </Box>
                  </InputAdornment>
                )
              }
            />
          )}
        />
      </AutocompleteContainer>
      {error && provider === 'elevenlabs' && (
        <ErrorMessage>
          {error?.response?.data?.error || error?.message}. Please ensure your API key is valid.
        </ErrorMessage>
      )}
      {error && provider !== 'elevenlabs' && (
        <ErrorMessage>{error?.response?.data?.error || error?.message}</ErrorMessage>
      )}
    </Container>
  );
};
